import { useCallback, useMemo } from 'react'
import { CommandType } from '~/constants/commands'
import { useAppConfig, useFeatureFlags } from '~/contexts/AppConfigContext'
import { useBillingContext } from '~/contexts/BillingContext'
import { commandTypeFromJSON } from '~/protos/agents'

// Default fallback commands when permissions haven't loaded yet
const DEFAULT_COMMANDS = new Set<CommandType>([
  CommandType.QUESTION_ANSWERING,
  CommandType.GENERATE_SIMPLE_CODE_PR,
  CommandType.MULE_VERSION_UPGRADE,
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION,
  CommandType.GENERATE_DATAWEAVE,
  CommandType.MUNIT_TEST_CREATION,
  CommandType.DATAWEAVE_TO_DATA_CREATION,
  CommandType.GENERATE_ADD_FLOW_CODE_PR,
  CommandType.GENERATE_REPO_ADD_FLOW_CODE_PR,
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE,
  CommandType.GENERATE_DATAWEAVE_REPO_AWARE,
  CommandType.DATAWEAVE_TO_DATA_CREATION_REPO_AWARE,
  CommandType.MULTI_REPO_QA,
  CommandType.GENERATE_MULTI_REPO_FLOW_DIAGRAM,
  CommandType.GENERAL_MULTI_REPO_QA_DEBUG_AGENT,
  CommandType.GENERATE_DOCUMENTATION,
  CommandType.GENERATE_FULL_REPO_TECH_MANIFEST_DOCUMENTATION,
  CommandType.GENERATE_MAPPING_TABLE,
  CommandType.GENERATE_IMAGE
])

export function useCommandPermissions() {
  const { config } = useAppConfig()
  const { flags } = useFeatureFlags()
  const { loaded, permittedCommands: backendPermittedCommands } = useBillingContext()

  // Use backend-provided permitted commands (available to all users)
  // Convert string command names from backend to CommandType enum values
  const permittedCommands = useMemo(() => {
    if (backendPermittedCommands?.permitted_commands?.length) {
      const commandTypes = backendPermittedCommands.permitted_commands
        .map(cmdName => commandTypeFromJSON(cmdName))
        .filter(cmd => cmd !== CommandType.UNRECOGNIZED)
      return new Set<CommandType>(commandTypes)
    }
    // Default fallback if permissions haven't loaded yet
    return DEFAULT_COMMANDS
  }, [backendPermittedCommands])

  const isCommandEnabled = useCallback(
    (command: CommandType): boolean => {
      // Permissions not yet loaded
      if (!loaded) {
        return false
      }

      // Check feature flag for specific commands
      if (
        command === CommandType.MULESOFT_TO_CAMEL_MIGRATION &&
        !config.mulesoft_to_camel_enabled
      ) {
        return false
      }

      // Spring Integration refactor agent has two gates that are OR'd:
      //   1. ``config.spring_integration_refactoring_enabled`` — global,
      //      deployment-wide kill switch from ``/api/config/app``.
      //   2. ``flags.spring_integration_refactoring`` — per-user allowlist
      //      from ``/api/config/features`` (users in
      //      ``SPRING_INTEGRATION_REFACTORING_USERS`` or
      //      ``ALL_ACCESS_USERS``). Mirrors the convention used by the
      //      other per-user feature flags above.
      // Hiding the command card here is enough — none of the SI backend
      // pipeline (the completeness gate, the curated examples KB, the
      // GitHub sample fetcher, the per-phase parallelism, the per-API
      // codegen) can run when the user can't trigger it.
      if (
        command === CommandType.SPRING_INTEGRATION_REFACTORING &&
        !config.spring_integration_refactoring_enabled &&
        !flags?.spring_integration_refactoring
      ) {
        return false
      }

      // Check if command is in the permitted commands set
      return permittedCommands.has(command)
    },
    [config, flags, permittedCommands, loaded]
  )

  return { isCommandEnabled }
}
